import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BUSINESS } from "../data";

// kind: "anchor" scrolls within home; "route" navigates to a different route
const NAV_ITEMS = [
  { label: "Services", target: "#services", kind: "anchor" },
  { label: "About", target: "#about", kind: "anchor" },
  { label: "Instagram", target: "/instagram", kind: "route" },
  { label: "Reviews", target: "#testimonials", kind: "anchor" },
  { label: "Contact", target: "#contact", kind: "anchor" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // when an anchor is clicked from a non-home route, route to home with hash
  const handleAnchorClick = (e, hash) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/" + hash);
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#0A0C10]/75 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <div className="relative h-8 w-8 rounded-sm bg-[#00F0FF] grid place-items-center">
            <span className="font-heading text-[#0A0C10] font-bold text-lg leading-none">
              A
            </span>
            <div className="absolute inset-0 rounded-sm bg-[#00F0FF] blur-md opacity-40 group-hover:opacity-70 transition-opacity" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-heading text-white text-[15px] font-semibold tracking-tight">
              AutoTech UK
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-0.5">
              Car Coding · London
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) =>
            item.kind === "route" ? (
              <Link
                key={item.target}
                to={item.target}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="text-sm text-[#9CA3AF] hover:text-white transition-colors tracking-tight"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.target}
                href={item.target}
                onClick={(e) => handleAnchorClick(e, item.target)}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="text-sm text-[#9CA3AF] hover:text-white transition-colors tracking-tight"
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-btn"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
          >
            <Phone className="h-3.5 w-3.5" />
            <span className="tabular-nums">{BUSINESS.phone}</span>
          </a>
          <a
            href={BUSINESS.whatsappText}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#0A0C10] font-semibold text-sm px-4 py-2 rounded-sm transition-colors"
          >
            WhatsApp
          </a>
          <button
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#0A0C10]/95 backdrop-blur-xl border-t border-white/5"
          data-testid="nav-mobile-menu"
        >
          <div className="px-5 py-6 flex flex-col gap-5">
            {NAV_ITEMS.map((item) =>
              item.kind === "route" ? (
                <Link
                  key={item.target}
                  to={item.target}
                  onClick={() => setOpen(false)}
                  className="text-base text-white/90 hover:text-[#00F0FF] transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.target}
                  href={item.target}
                  onClick={(e) => handleAnchorClick(e, item.target)}
                  className="text-base text-white/90 hover:text-[#00F0FF] transition-colors"
                >
                  {item.label}
                </a>
              )
            )}
            <a
              href={BUSINESS.whatsappText}
              target="_blank"
              rel="noreferrer"
              className="bg-[#00F0FF] text-[#0A0C10] font-semibold text-sm px-4 py-3 rounded-sm text-center"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
